// PRD-010 T2.2 (GREEN) + T5.4 (DOCS): HistoryDispatchAdapter — lifecycle-managed
// wrapper around an inner History-like object's `push` method. See the
// class-level JSDoc below for Findings 8 + 9 rationale.

/**
 * Minimal structural type of the inner object whose `push` method we wrap.
 * Kept local so the adapter doesn't pull in @o2d/history typings — the
 * adapter is intentionally agnostic about the concrete History class.
 */
export interface PushableHistory<TCommand, TState> {
  push: (cmd: TCommand, state: TState) => void;
}

/**
 * Callback fired after every successful push (both via the wrapped
 * `inner.push` and via the explicit `adapter.push`).
 */
export type DispatchCallback<TCommand, TState, TExtras = unknown> = (
  cmd: TCommand,
  state: TState,
  extras?: TExtras,
) => void;

/**
 * Wraps History.push with a dispatch callback via monkey-patching (Approach A).
 *
 * Pattern: History-Push-Interception via Monkey-Patch.
 *
 * Rationale for Approach A vs. Approach B:
 *   @o2d/history exposes only `push / undo / redo / clear / onChange`.
 *   `onChange` is parameter-less and fires for undo/redo too — it cannot
 *   identify which command was just applied. Approach B (modifying @o2d/history
 *   to expose an `afterPush(cmd, state)` hook) was rejected because it would
 *   couple the history package to the dispatch concern, violating the
 *   single-responsibility of @o2d/history.
 *   Approach A (intercept push in the consumer) keeps the history package
 *   agnostic and allows the adapter to be tested in isolation.
 *
 * See PRD-009 §Wiring Approach Decision and ADR-003
 * (`docs/adr/ADR-003-monkey-patch-lifecycle.md`).
 *
 * Lifecycle contract:
 *   - Call attach() in connectedCallback() — installs the push wrapper.
 *   - Call detach() in disconnectedCallback() — MUST be called to prevent
 *     memory leaks and stale event dispatches from detached elements.
 *     Failing to call detach() causes push-wrapper accumulation on every
 *     SPA remount cycle.
 *
 * Design Decision: Per-command dispatch without event coalescing.
 *
 * Rationale: Drag sessions accumulate all per-frame position deltas into a
 * single MoveObjectsCommand.moves[] array before pushing to History — they do
 * NOT emit one command per mouse-move event. Coalescing at the dispatch layer
 * would therefore be over-engineering for the current use case.
 *
 * If a future command kind needs coalescing, add an optional
 * `flushPendingCommands?(): void` method to HistoryDispatchAdapter rather than
 * retrofitting a global queue. See PRD-010 Finding 8 and ADR-003 for full
 * rationale.
 */
export class HistoryDispatchAdapter<TCommand, TState, TExtras = unknown> {
  private _attached = false;
  private _originalPush: ((cmd: TCommand, state: TState) => void) | null = null;

  constructor(
    private readonly _inner: PushableHistory<TCommand, TState>,
    private readonly _onPush: DispatchCallback<TCommand, TState, TExtras>,
  ) {}

  /** Install the push wrapper. Idempotent. */
  attach(): void {
    if (this._attached) return;
    const inner = this._inner;
    const original = inner.push;
    this._originalPush = original;
    const onPush = this._onPush;
    inner.push = (cmd: TCommand, state: TState): void => {
      original.call(inner, cmd, state);
      onPush(cmd, state, undefined);
    };
    this._attached = true;
  }

  /** Restore the original push by reference equality. Idempotent. */
  detach(): void {
    if (!this._attached || this._originalPush === null) return;
    this._inner.push = this._originalPush;
    this._originalPush = null;
    this._attached = false;
  }

  /**
   * Explicit push API that bypasses the monkey-patched wrapper. Use this when
   * the call site needs to carry typed extras (e.g. DuplicateObjectsCommand
   * sourceUids) — extras flow as an immutable parameter rather than via a
   * shared mutable field on the element (Pattern A, PRD-010 Finding 3).
   */
  push(cmd: TCommand, state: TState, extras?: TExtras): void {
    const original = this._originalPush;
    if (original !== null) {
      original.call(this._inner, cmd, state);
    } else {
      this._inner.push(cmd, state);
    }
    this._onPush(cmd, state, extras);
  }
}
